'use client';

import { useEffect } from 'react';
import { cn } from '@/lib/utils';
import type { ModuleId } from '../../types';
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  TrendingUp,
  ShoppingBag,
  Calculator,
  Factory,
  Users,
  FolderKanban,
  Settings,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react';

interface SidebarProps {
  currentModule: ModuleId;
  onModuleChange: (module: ModuleId) => void;
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface MenuItem {
  id: ModuleId;
  label: string;
  icon: React.ReactNode;
}

const menuItems: MenuItem[] = [
  { id: 'dashboard', label: '대시보드', icon: <LayoutDashboard className="w-5 h-5" /> },
  { id: 'pos', label: 'POS', icon: <ShoppingCart className="w-5 h-5" /> },
  { id: 'stock', label: '재고 관리', icon: <Package className="w-5 h-5" /> },
  { id: 'selling', label: '판매 관리', icon: <TrendingUp className="w-5 h-5" /> },
  { id: 'buying', label: '구매 관리', icon: <ShoppingBag className="w-5 h-5" /> },
  { id: 'accounts', label: '회계 관리', icon: <Calculator className="w-5 h-5" /> },
  { id: 'manufacturing', label: '제조 관리', icon: <Factory className="w-5 h-5" /> },
  { id: 'crm', label: 'CRM', icon: <Users className="w-5 h-5" /> },
  { id: 'projects', label: '프로젝트', icon: <FolderKanban className="w-5 h-5" /> },
];

const bottomMenuItems: MenuItem[] = [
  { id: 'settings', label: '설정', icon: <Settings className="w-5 h-5" /> },
];

export function Sidebar({
  currentModule,
  onModuleChange,
  collapsed,
  onToggleCollapse,
  mobileOpen = false,
  onMobileClose,
}: SidebarProps) {
  // 모바일에서 메뉴 선택 시 사이드바 닫기
  const handleModuleChange = (module: ModuleId) => {
    onModuleChange(module);
    if (onMobileClose) {
      onMobileClose();
    }
  };

  // 모바일 사이드바 열렸을 때 스크롤 방지
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileOpen]);

  return (
    <>
      {/* 모바일 오버레이 */}
      {mobileOpen && (
        <div
          className="absolute inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* 사이드바 */}
      <aside
        className={cn(
          'bg-card border-r border-border flex-col transition-all duration-300 h-full',
          // 데스크톱: 고정 사이드바
          'hidden lg:flex',
          collapsed ? 'lg:w-16' : 'lg:w-56',
          // 모바일: 오버레이 사이드바 (프로젝트 영역 내에서만)
          mobileOpen ? 'absolute inset-y-0 left-0 z-50 flex w-64' : ''
        )}
      >
        {/* 로고 + 모바일 닫기 버튼 */}
        <div className="h-14 flex items-center justify-between px-3 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Package className="w-5 h-5 text-primary-foreground" />
            </div>
            {(!collapsed || mobileOpen) && <span className="font-bold">Box ERP</span>}
          </div>
          {/* 모바일 닫기 버튼 */}
          {mobileOpen && (
            <button
              onClick={onMobileClose}
              className="p-1.5 rounded-lg hover:bg-accent lg:hidden"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* 메인 메뉴 */}
        <nav className="flex-1 py-3 overflow-y-auto">
          <ul className="space-y-1 px-2">
            {menuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => handleModuleChange(item.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    currentModule === item.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground'
                  )}
                  title={collapsed && !mobileOpen ? item.label : undefined}
                >
                  {item.icon}
                  {(!collapsed || mobileOpen) && <span className="text-sm font-medium">{item.label}</span>}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* 하단 메뉴 */}
        <div className="border-t border-border py-3">
          <ul className="space-y-1 px-2">
            {bottomMenuItems.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => handleModuleChange(item.id)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    currentModule === item.id
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground'
                  )}
                  title={collapsed && !mobileOpen ? item.label : undefined}
                >
                  {item.icon}
                  {(!collapsed || mobileOpen) && <span className="text-sm font-medium">{item.label}</span>}
                </button>
              </li>
            ))}
          </ul>

          {/* 접기/펼치기 버튼 - 데스크톱에서만 */}
          <div className="px-2 mt-2 hidden lg:block">
            <button
              onClick={onToggleCollapse}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-border text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors text-sm"
            >
              {collapsed ? (
                <ChevronRight className="w-5 h-5" />
              ) : (
                <>
                  <ChevronLeft className="w-5 h-5" />
                  <span>접기</span>
                </>
              )}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
